import type { components } from '../api/active/generated/openapi';
import { randomUuid } from '../api/active/randomUuid';
import type { ActiveTransport } from '../api/active/transport';
import type {
  AssignmentPatch,
  AssignmentWrite,
  AuditEvent,
  EffectiveAuthorization,
  ManagedRole,
  ManagementCatalog,
  ManagementRolePolicy,
  ManagementRolePolicyAuditEvent,
  Page,
  PendingMemberRegistration,
  RoleAssignment,
  RoleDraft,
  RoleModulePolicy,
  RolePreview,
  UserPatch,
} from './managementModel';
import {
  type AdministratedDepartment,
  PermissionAdminError,
  type RolePolicyWrite,
  type UserAuthorizationWrite,
  type WeComMemberSyncResult,
} from './permissionAdminApi';

export type PendingManagementWrite = Readonly<{
  status: 'sending' | 'uncertain';
  label: string;
}>;
type WriteCommand = {
  signature: string;
  path: string;
  method: string;
  bodyJson?: string;
  version?: string;
  key: string;
  promise?: Promise<unknown>;
};
type WriteState = {
  command: WriteCommand | null;
  snapshot: PendingManagementWrite | null;
  listeners: Set<() => void>;
};
const ownerWrites = new WeakMap<ActiveTransport, Map<string, WriteState>>();
function writeState(transport: ActiveTransport, owner?: string): WriteState {
  const fresh = (): WriteState => ({
    command: null,
    snapshot: null,
    listeners: new Set(),
  });
  if (!owner) return fresh();
  let scopes = ownerWrites.get(transport);
  if (!scopes) {
    scopes = new Map();
    ownerWrites.set(transport, scopes);
  }
  let state = scopes.get(owner);
  if (!state) {
    state = fresh();
    scopes.set(owner, state);
  }
  return state;
}
function commandLabel(path: string, method: string) {
  if (path === '/management-role-policy') return '发布管理员共享权限';
  if (path === '/users:sync-wecom') return '同步企业微信成员';
  if (path.startsWith('/departments'))
    return method === 'POST' ? '新增部门' : '更新部门';
  if (path.startsWith('/role-assignments'))
    return path.endsWith(':revoke')
      ? '撤销角色授权'
      : method === 'POST'
        ? '新增角色授权'
        : '更新角色授权';
  if (path.startsWith('/users'))
    return method === 'POST' ? '登记成员' : '更新成员';
  if (path.endsWith(':publish')) return '发布角色策略';
  if (method === 'PATCH' && path.startsWith('/roles/'))
    return '更新现有部门角色权限';
  return method === 'POST' ? '新建角色草稿' : '保存角色草稿';
}
/** A single unresolved write per authenticated owner/view; no permission facts are cached here. */
export function createManagementApi(
  transport: ActiveTransport,
  csrfToken: string | null,
  recoverUnauthorized?: () => Promise<void>,
  owner?: string,
) {
  const state = writeState(transport, owner);
  const publish = (status: PendingManagementWrite['status'] | null) => {
    state.snapshot =
      status && state.command
        ? {
            status,
            label: commandLabel(state.command.path, state.command.method),
          }
        : null;
    for (const listener of state.listeners) listener();
  };
  async function request<T>(
    path: string,
    method = 'GET',
    body?: unknown,
    version?: string,
    signal?: AbortSignal,
  ): Promise<T> {
    const mutation = method !== 'GET' && !path.endsWith('/draft:preview');
    const bodyJson = body === undefined ? undefined : JSON.stringify(body);
    const signature = JSON.stringify([path, method, bodyJson, version]);
    let command: WriteCommand | undefined;
    if (mutation) {
      if (state.command && state.command.signature !== signature)
        throw new PermissionAdminError(
          409,
          '上次提交结果尚未确认，请先重试确认原提交。',
          'MANAGEMENT_WRITE_PENDING',
        );
      command = state.command ?? {
        signature,
        path,
        method,
        bodyJson,
        version,
        key: randomUuid(),
      };
      if (command.promise) return command.promise as Promise<T>;
      state.command = command;
      publish('sending');
    }
    const current = command;
    const execute = async (): Promise<T> => {
      try {
        const result = await transport.requestResponse(path, {
          method,
          cache: 'no-store',
          signal,
          headers:
            method === 'GET'
              ? undefined
              : {
                  ...(bodyJson !== undefined
                    ? { 'Content-Type': 'application/json' }
                    : {}),
                  'Idempotency-Key': current?.key ?? randomUuid(),
                  ...(csrfToken ? { 'X-CSRF-Token': csrfToken } : {}),
                  ...(version ? { 'If-Match': `"${version}"` } : {}),
                },
          ...(bodyJson === undefined ? {} : { body: bodyJson }),
        });
        if (!result.ok) {
          const problem = (await result.json().catch(() => ({}))) as {
            code?: string;
            detail?: string;
            title?: string;
          };
          if (
            current &&
            result.status >= 400 &&
            result.status < 500 &&
            problem.code !== 'IDEMPOTENCY_IN_PROGRESS'
          ) {
            state.command = null;
            publish(null);
          }
          if (result.status === 401) await recoverUnauthorized?.();
          throw new PermissionAdminError(
            result.status,
            problem.detail ??
              problem.title ??
              `请求失败（HTTP ${result.status}）。`,
            problem.code,
          );
        }
        const payload = (await result.json()) as T;
        if (current && state.command === current) {
          state.command = null;
          publish(null);
        }
        return payload;
      } catch (error) {
        if (current && state.command === current) publish('uncertain');
        throw error;
      }
    };
    const promise = execute();
    if (current) current.promise = promise;
    try {
      return await promise;
    } finally {
      if (current?.promise === promise) current.promise = undefined;
    }
  }
  const rolePath = (id: string) => `/roles/${encodeURIComponent(id)}`;
  const assignmentPath = (id: string) =>
    `/role-assignments/${encodeURIComponent(id)}`;
  return {
    getPendingWrite: () => state.snapshot,
    subscribePendingWrite: (listener: () => void) => {
      state.listeners.add(listener);
      return () => {
        state.listeners.delete(listener);
      };
    },
    retryPendingWrite: async () => {
      const command = state.command;
      if (!command) return;
      return request(
        command.path,
        command.method,
        command.bodyJson === undefined
          ? undefined
          : JSON.parse(command.bodyJson),
        command.version,
      );
    },
    syncWeComMembers: () =>
      request<WeComMemberSyncResult>('/users:sync-wecom', 'POST'),
    registerMember: (body: PendingMemberRegistration) =>
      request('/users', 'POST', {
        ...body,
        status: 'pending_authorization',
        authorizationSource: 'assignments',
      }),
    catalog: (signal?: AbortSignal) =>
      request<ManagementCatalog>(
        '/permissions',
        'GET',
        undefined,
        undefined,
        signal,
      ),
    roles: (signal?: AbortSignal) =>
      request<{ items: ManagedRole[] }>(
        '/roles',
        'GET',
        undefined,
        undefined,
        signal,
      ),
    createDepartment: (
      body: components['schemas']['DepartmentCreateRequest'],
    ) => request<AdministratedDepartment>('/departments', 'POST', body),
    patchDepartment: (
      id: string,
      version: string,
      body: { name?: string; active?: boolean; defaultRoleId?: string | null },
    ) =>
      request<AdministratedDepartment>(
        `/departments/${encodeURIComponent(id)}`,
        'PATCH',
        body,
        version,
      ),
    patchUser: (id: string, version: string, body: UserPatch) =>
      request(`/users/${encodeURIComponent(id)}`, 'PATCH', body, version),
    updateLegacyDepartmentAuthorization: (
      id: string,
      version: string,
      body: UserAuthorizationWrite,
    ) => request(`/users/${encodeURIComponent(id)}`, 'PATCH', body, version),
    updateLegacyRolePolicy: async (
      id: string,
      version: string,
      body: RolePolicyWrite,
    ) => {
      const role = await request<ManagedRole>(
        `/roles/${encodeURIComponent(id)}`,
        'PATCH',
        body,
        version,
      );
      return { role, etag: `"${role.rowVersion}"` };
    },
    effective: (id: string) =>
      request<EffectiveAuthorization>(
        `/users/${encodeURIComponent(id)}/effective-authorization`,
      ),
    createRole: (body: { key: string; name: string; active: boolean }) =>
      request<RoleDraft>('/roles/drafts', 'POST', {
        ...body,
        authorizationModelVersion: '3',
      }),
    draft: (id: string) => request<RoleDraft>(`${rolePath(id)}/draft`),
    saveDraft: (draft: RoleDraft) =>
      request<RoleDraft>(
        `${rolePath(draft.roleId)}/draft`,
        'PUT',
        {
          name: draft.name,
          active: draft.active,
          modulePolicies: draft.modulePolicies,
          managementPermissions: draft.managementPermissions ?? [],
        },
        draft.draftRowVersion,
      ),
    preview: (id: string, version: string) =>
      request<RolePreview>(
        `${rolePath(id)}/draft:preview`,
        'POST',
        undefined,
        version,
      ),
    publish: (id: string, preview: RolePreview) =>
      request<{ publishedVersion: string }>(
        `${rolePath(id)}/draft:publish`,
        'POST',
        {
          draftRowVersion: preview.draftRowVersion,
          previewDigest: preview.previewDigest,
        },
        preview.draftRowVersion,
      ),
    versions: (id: string) =>
      request<{
        items: Array<{
          publishedVersion: string;
          name: string;
          active: boolean;
          publishedAt?: string;
          modulePolicies: RoleModulePolicy[];
        }>;
      }>(`${rolePath(id)}/published-versions`),
    getManagementRolePolicy: () =>
      request<ManagementRolePolicy>('/management-role-policy'),
    publishManagementRolePolicy: (
      version: string,
      body: Omit<ManagementRolePolicy, 'rowVersion'>,
    ) =>
      request<ManagementRolePolicy>(
        '/management-role-policy',
        'PATCH',
        body,
        version,
      ),
    managementRolePolicyAudit: (page = 1) =>
      request<Page<ManagementRolePolicyAuditEvent>>(
        `/management-role-policy/audit-events?page=${page}&pageSize=20`,
      ).then((result) => ({
        ...result,
        items: result.items.map((event) => ({
          ...event,
          objectType: '管理员权限发布',
          result:
            event.resultCode === 'MANAGEMENT_ROLE_POLICY_PUBLISHED'
              ? 'success'
              : '已记录',
        })),
      })),
    roleAudit: (id: string, page = 1) =>
      request<Page<AuditEvent>>(
        `${rolePath(id)}/audit-events?page=${page}&pageSize=20`,
      ),
    assignments: (
      filters: {
        userId?: string;
        roleId?: string;
        status?: string;
        page?: number;
      } = {},
    ) => {
      const query = new URLSearchParams({
        page: String(filters.page ?? 1),
        pageSize: '20',
      });
      for (const key of ['userId', 'roleId', 'status'] as const)
        if (filters[key]) query.set(key, filters[key]);
      return request<Page<RoleAssignment>>(`/role-assignments?${query}`);
    },
    assignment: (id: string) => request<RoleAssignment>(assignmentPath(id)),
    createAssignment: (body: AssignmentWrite) =>
      request<RoleAssignment>('/role-assignments', 'POST', body),
    patchAssignment: (id: string, version: string, body: AssignmentPatch) =>
      request<RoleAssignment>(assignmentPath(id), 'PATCH', body, version),
    revokeAssignment: (id: string, version: string) =>
      request<RoleAssignment>(
        `${assignmentPath(id)}:revoke`,
        'POST',
        undefined,
        version,
      ),
    assignmentAudit: async (
      id: string,
      page = 1,
    ): Promise<Page<AuditEvent>> => {
      const result = await request<
        Page<{
          actorUserId: string;
          occurredAt: string;
          action: string;
          resultCode?: string;
        }>
      >(`${assignmentPath(id)}/audit-events?page=${page}&pageSize=20`);
      const labels: Record<string, string> = {
        'identity.create-role-assignment': '新增角色授权',
        'identity.update-role-assignment': '更新角色授权',
        'identity.revoke-role-assignment': '撤销角色授权',
      };
      return {
        ...result,
        items: result.items.map((event) => ({
          actorUserId: event.actorUserId,
          occurredAt: event.occurredAt,
          objectType: labels[event.action] ?? '角色授权变更',
          result:
            event.resultCode === 'ASSIGNMENT_UPDATED' ? 'success' : '已记录',
        })),
      };
    },
  };
}
export type ManagementApi = ReturnType<typeof createManagementApi>;
